// src/components/AssignWorkersModal.jsx
import React, { useEffect, useState } from 'react';
import authFetch from '../api';

const AssignWorkersModal = ({ projectId, onClose, onAssign, assignedWorkers = [], translations = {} }) => {
  const [workers, setWorkers] = useState([]);
  const [selectedWorkers, setSelectedWorkers] = useState([...assignedWorkers]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchWorkers = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const res = await authFetch('/workers');
        console.log('📊 Workers API response:', res);
        
        // Handle different response structures
        let workersData;
        if (res && res.data) {
          workersData = res.data;
        } else if (Array.isArray(res)) {
          workersData = res;
        } else {
          workersData = [];
        }
        
        console.log('📊 Processed workers data:', workersData);
        setWorkers(Array.isArray(workersData) ? workersData : []);
      } catch (err) {
        console.error('❌ Failed to fetch workers:', err);
        setError('Failed to load workers');
        setWorkers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchWorkers();
  }, []);

  const toggleSelection = (workerId) => {
    setSelectedWorkers((prev) =>
      prev.includes(workerId) ? prev.filter((id) => id !== workerId) : [...prev, workerId]
    );
  };

  // Filter workers based on search term
  const filteredWorkers = workers.filter(worker =>
    worker.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    worker.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    worker.phone?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get currently selected workers from filtered list
  const selectedFilteredWorkers = filteredWorkers.filter(worker => 
    selectedWorkers.includes(worker._id)
  );

  const handleSelectAll = () => {
    const allFilteredIds = filteredWorkers.map(worker => worker._id);
    setSelectedWorkers(prev => {
      const newSelection = [...prev];
      allFilteredIds.forEach(id => {
        if (!newSelection.includes(id)) {
          newSelection.push(id);
        }
      });
      return newSelection;
    });
  };

  const handleClearAll = () => {
    const allFilteredIds = filteredWorkers.map(worker => worker._id);
    setSelectedWorkers(prev => prev.filter(id => !allFilteredIds.includes(id)));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div className="bg-white p-8 rounded-2xl shadow-2xl w-full max-w-xl border border-gray-100 animate-fade-in">
        <h2 className="text-2xl font-bold mb-6 text-[#173861]">{translations.title || 'Assign Workers'}</h2>
        
        {/* Search Field */}
        {!loading && !error && workers.length > 0 && (
          <div className="mb-4">
            <div className="relative">
              <input
                type="text"
                placeholder={translations.searchPlaceholder || "Search workers by name, email, or phone..."}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
            {searchTerm && (
              <div className="mt-2 text-sm text-gray-600">
                Found {filteredWorkers.length} worker{filteredWorkers.length !== 1 ? 's' : ''} 
                {filteredWorkers.length !== workers.length && ` out of ${workers.length} total`}
              </div>
            )}
            {filteredWorkers.length > 0 && (
              <div className="mt-2 flex gap-2">
                <button
                  onClick={handleSelectAll}
                  className="text-sm text-blue-600 hover:text-blue-800 underline"
                >
                  {translations.selectAll || 'Select All'} ({filteredWorkers.length})
                </button>
                <button
                  onClick={handleClearAll}
                  className="text-sm text-red-600 hover:text-red-800 underline"
                >
                  {translations.clearAll || 'Clear All'}
                </button>
                {selectedFilteredWorkers.length > 0 && (
                  <span className="text-sm text-gray-600 ml-auto">
                    {selectedFilteredWorkers.length} selected
                  </span>
                )}
              </div>
            )}
          </div>
        )}
        
        {loading && (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent mx-auto mb-4"></div>
            <p className="text-gray-600">Loading workers...</p>
          </div>
        )}
        
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">{error}</p>
          </div>
        )}
        
        {!loading && !error && (
          <div className="space-y-3 max-h-60 overflow-y-auto mb-6">
            {workers.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No workers available</p>
            ) : filteredWorkers.length === 0 ? (
              <div className="text-center py-4">
                <p className="text-gray-500">No workers found matching "{searchTerm}"</p>
                <button
                  onClick={() => setSearchTerm('')}
                  className="mt-2 text-blue-600 hover:text-blue-800 underline"
                >
                  Clear search
                </button>
              </div>
            ) : (
              filteredWorkers.map((worker) => (
                <label key={worker._id} className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50 transition">
                  <input
                    type="checkbox"
                    checked={selectedWorkers.includes(worker._id)}
                    onChange={() => toggleSelection(worker._id)}
                    className="accent-[#173861] w-5 h-5 rounded"
                  />
                  <div className="flex flex-col">
                    <span className="text-base font-medium">{worker.name}</span>
                    {(worker.email || worker.phone) && (
                      <span className="text-sm text-gray-500">
                        {worker.email && worker.phone ? `${worker.email} • ${worker.phone}` : worker.email || worker.phone}
                      </span>
                    )}
                  </div>
                </label>
              ))
            )}
          </div>
        )}
        
        {/* Selected Workers Summary */}
        {selectedWorkers.length > 0 && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="text-sm font-medium text-blue-800 mb-2">
              Selected Workers ({selectedWorkers.length}):
            </div>
            <div className="text-sm text-blue-700 max-h-20 overflow-y-auto">
              {workers
                .filter(worker => selectedWorkers.includes(worker._id))
                .map(worker => worker.name)
                .join(', ')}
            </div>
          </div>
        )}
        
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 font-semibold transition"
          >
            {translations.cancel || 'Cancel'}
          </button>
          <button
            onClick={() => {
              const newAssignments = selectedWorkers.filter(id => !assignedWorkers.includes(id));
              onAssign([...assignedWorkers, ...newAssignments]);
              onClose();
            }}
            className="px-5 py-2 rounded-lg bg-[#173861] text-white hover:bg-green-600 font-semibold shadow transition"
          >
                          {translations.assign || 'Assign'} ({selectedWorkers.length})
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignWorkersModal;